import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Chip, ShieldIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { COMPREHENSIVE, SIGNATURE, VARIANT_FLOW } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { CoverLevelBenefits } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";
import { DynamicCoverLimit } from "@/types/DynamicCoverLimitType";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";
import { resolveMagnoliaAssetUrl } from "@/utils/cms-image-resolver";
import { getCoverLevelBenefits } from "@/utils/get-cover-level-info";
import { quoteShouldHideField } from "@/utils/quote-brand-impact.utlls";

type CoverLevelProps = {
  cmsData: {
    coverLevelTitle: string;
    coverLevelEditButtonLabel: string;
    coverLevelSectionText: string;
    dashBoardCoverLevelSectionChipLabel?: string;
    coverLevelEditButtonABTestLabel?: string;
    coverLevelBenefits: CoverLevelBenefits;
  };
};

function CoverLevel({ cmsData }: Readonly<CoverLevelProps>) {
  const router = useRouter();
  const {
    coverLevelTitle,
    coverLevelEditButtonLabel,
    coverLevelSectionText,
    coverLevelBenefits,
    dashBoardCoverLevelSectionChipLabel,
    coverLevelEditButtonABTestLabel,
  } = cmsData;

  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes } = quoteData;
  const { tripDetails, priceResult } = attributes;
  const policyPackage = tripDetails?.policyPackage;
  const policyType = tripDetails?.policyType;
  const dynamicCoverLimit = priceResult?.dynamicCoverLimit;
  const renewalDashBoardCoverLevelSection = localStorage.getItem(
    OptimizelyExperimentsEnum.RENEWAL_DASHBOARD_COVER_LEVEL_SECTION
  );
  const renewalCurrentPolicyPageSkip = localStorage.getItem(
    OptimizelyExperimentsEnum.RENEWAL_CURRENT_POLICY_PAGE_SKIP
  );

  const isMobile = useMediaQuery("(max-width: 599px)");

  const { updateLastEditAccordion } = useRenewalStateContext();

  const extractSelectedCancellationCoverValue = (
    dynamicCoversLimit: DynamicCoverLimit[],
    tripType: string | undefined,
    coverType: string | undefined
  ) => {
    const selectedDisplayValue = dynamicCoversLimit
      .flatMap((cover) =>
        cover.product.filter(
          (item) =>
            item.policyType === tripType && item.policyPackage === coverType
        )
      )
      .flatMap((product) => product.values)
      .find((value) => value.selected)?.displayValue;

    return selectedDisplayValue ?? null;
  };

  const renderUpgradeChip = () => {
    return (
      <Chip
        type="complete"
        variant="positive"
        label={dashBoardCoverLevelSectionChipLabel ?? "Upgrade Available"}
        className={`!border-none !rounded-radius-full ${
          isMobile ? "" : "ml-[5px]"
        }`}
        labelStyles="w-max text-3xs font-[600] font-body-font leading-[20px]"
        icon={
          <ShieldIcon
            fill={selectedColorBrand["icon-positive"]}
            stroke={selectedColorBrand["icon-positive"]}
            width={16}
            height={16}
            strokeWidth={0}
          />
        }
      />
    );
  };

  const getBenefits = (data: CoverLevelBenefits, coverType?: string) => {
    const cover = getCoverLevelBenefits(
      data,
      coverType ?? "",
      tripDetails?.goingOnCruise && isCruiseImpact
    ); // Find the matching coverType

    if (cover) {
      const benefitCopies = cover.benefitCopy;
      const benefits = Object.keys(benefitCopies)

        .filter((key) => !isNaN(key as unknown as number)) // Only numeric keys
        .map((key) => benefitCopies[key]); // Map to benefit objects
      return {
        coverName: cover.coverName,
        coverType: cover.coverType,
        whichBestBuyBadge: cover.whichBestBuyBadgeImage,
        benefits,
      };
    }

    return null; // If no matching coverType is found
  };

  const result = getBenefits(coverLevelBenefits, policyPackage);

  const { current } = useWizard();
  const isDisplayWhichBestBuyBadge =
    current.brandSpecificParams?.isDisplayWhichBestBuyBadge;
  const whichBestBuyBadge = result?.whichBestBuyBadge;

  const renewalBadgeSrc = useMemo(
    () => (whichBestBuyBadge ? resolveMagnoliaAssetUrl(whichBestBuyBadge) : ""),
    [whichBestBuyBadge]
  );

  const shouldShowWhichBadge = useMemo(() => {
    const isEligibleCoverLevel =
      result?.coverType === COMPREHENSIVE || result?.coverType === SIGNATURE;
    return Boolean(
      isDisplayWhichBestBuyBadge &&
        whichBestBuyBadge &&
        whichBestBuyBadge.trim().length > 0 &&
        isEligibleCoverLevel
    );
  }, [isDisplayWhichBestBuyBadge, whichBestBuyBadge, result?.coverType]);

  return (
    <div id="cover-level-main-section">
      {renewalDashBoardCoverLevelSection !== VARIANT_FLOW && (
        <>
          <p className="font-body-font text-2xs font-[400] leading-[24px] text-text-functional-subtle">
            {coverLevelTitle}
          </p>
          <div className="flex justify-between items-start pb-spacing-xs">
            <div className="flex flex-col md:flex-row gap-[8px] md:gap-[16px] max-w-[128px] md:max-w-[229px] md:items-center">
              <p className="font-body-font text-xs font-[700] leading-[27px] text-interactive-secondary-default">
                {result?.coverName}
              </p>
              {shouldShowWhichBadge && (
                <div className="pointer-events-none md:my-[-20px]">
                  <Image
                    loader={() => renewalBadgeSrc}
                    src={renewalBadgeSrc}
                    width={85}
                    height={67}
                    alt="Which? Best Buy"
                    objectFit="cover"
                  />
                </div>
              )}
            </div>
            <div>
              <LabelButtonWrapper
                label={coverLevelEditButtonLabel}
                hierarchy="tertiary"
                className="font-body-font leading-[18px] font-[600] text-xs realtive top-[-2px] !px-spacing-xxs"
                onClick={() => {
                  updateLastEditAccordion("policy-details");
                  // AB Test Logic: Skip Current Policy if variant flag is active
                  if (renewalCurrentPolicyPageSkip === VARIANT_FLOW) {
                    router.push("/cover-level");
                  } else {
                    router.push("/current-policy");
                  }
                }}
              />
            </div>
          </div>
        </>
      )}

      {renewalDashBoardCoverLevelSection === VARIANT_FLOW && (
        <>
          <div className=" flex flex-row justify-between md:mb-spacing-xxs mb-spacing-xs">
            <div>
              <p className="font-body-font text-2xs font-[400] leading-[24px] text-text-functional-subtle">
                {coverLevelTitle}
              </p>
            </div>
            {isMobile && policyPackage !== SIGNATURE && (
              <div>{renderUpgradeChip()}</div>
            )}
          </div>
          <div
            className={`flex md:flex-row flex-col  mb-spacing-xs px-spacing-s py-spacing-m bg-[#EFF4FF] border border-solid border-border-info-secondary rounded-[4px] ${
              isMobile
                ? "max-h-[85px] items-start"
                : "max-h-[60px] items-center"
            }`}
          >
            <div>
              <p className="font-body-font text-xs font-[700] leading-[27px] text-interactive-secondary-default">
                {result?.coverName}
              </p>
            </div>
            {!isMobile && policyPackage !== SIGNATURE && renderUpgradeChip()}
            <div className="md:ml-auto">
              <LabelButtonWrapper
                label={coverLevelEditButtonABTestLabel ?? "Compare all levels"}
                hierarchy="tertiary"
                className="font-body-font leading-[18px] font-[600] text-xs realtive top-[-2px] md:!px-spacing-xxs sm:!px-0"
                onClick={() => {
                  updateLastEditAccordion("policy-details");
                  if (renewalCurrentPolicyPageSkip === VARIANT_FLOW) {
                    router.push("/cover-level");
                  } else {
                    router.push("/current-policy");
                  }
                }}
              />
            </div>
          </div>
        </>
      )}

      <div>
        <p className="font-body-font text-2xs font-[400] leading-[24px] text-text-functional-1 py-[2px]">
          {coverLevelSectionText}
        </p>

        <ul className="list-disc [&>li]:marker:text-4xs ml-spacing-l">
          {result?.benefits
            .filter((item) => item.isShownInRenewals) // Only include items where isShownInRenewals is true
            .map((item, index) =>
              quoteShouldHideField(attributes?.tripDetails) &&
              item.isExcludeUK ? null : (
                <li
                  key={index as unknown as string}
                  className="font-body-font text-text-functional-1 text-2xs font-[400] leading-[24px] mt-spacing-xxs min-h-[28px]"
                >
                  {`${
                    item.isCancellation
                      ? extractSelectedCancellationCoverValue(
                          dynamicCoverLimit ?? [],
                          policyType,
                          policyPackage
                        )
                      : ""
                  } ${item.description}`}
                </li>
              )
            )}
        </ul>
      </div>
    </div>
  );
}

export default CoverLevel;
